import React, { ReactNode } from 'react';
import './HeaderScrollbar.css';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <style>{`
        .logo-wrapper > * {
          max-height: 100% !important;
          height: 100% !important;
          width: auto !important;
          display: block !important;
        }
        .logo-wrapper svg, .logo-wrapper img {
          max-height: 100% !important;
          height: 100% !important;
          width: auto !important;
          display: block !important;
        }
        .logo-wrapper {
          height: 29px !important;
        }
        @media (min-width: 768px) {
          .logo-wrapper {
            height: 33px !important;
          }
        }
      `}</style>
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="relative flex h-[52px] w-full items-center bg-[#363636]">
          <div className="z-10 flex h-full flex-shrink-0 items-center bg-[#363636] pl-0 pr-4">
            <div
              className={classNames(
                'inline-flex items-center',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {isReturnEnabled && <Icons.ArrowLeft className="text-primary ml-1 h-7 w-7" />}
              <div className="ml-0 p-0">
                <div className="logo-wrapper flex h-6 w-auto items-center overflow-hidden md:h-8 md:w-auto">
                  {WhiteLabeling?.createLogoComponentFn?.(React, props) || (
                    <Icons.OHIFLogo className="h-6 max-h-full w-auto md:h-8" />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex h-full min-w-0 flex-1 items-center">
            <div className="flex w-full items-center justify-center">
              <div
                className="relative flex h-full items-center"
                style={{ minWidth: 0, width: '100%' }}
              >
                <div
                  className="toolbar-scrollbar mx-auto flex h-full overflow-x-auto"
                  style={{
                    borderRadius: 12,
                    background: '#121212',
                    WebkitOverflowScrolling: 'touch',
                    boxSizing: 'border-box',
                    maxWidth: '100%',
                    paddingTop: 4,
                    paddingRight: 6,
                    paddingLeft: 6,
                  }}
                >
                  <div
                    className="flex w-max items-center space-x-6 px-2 py-0"
                    style={{ height: '100%' }}
                  >
                    {React.Children.map(children, child => {
                      if (
                        React.isValidElement(child) &&
                        child.type &&
                        typeof child.type === 'function' &&
                        'displayName' in child.type &&
                        child.type.displayName === 'ToolButton'
                      ) {
                        return React.cloneElement(child, {
                          size: 'small',
                          className:
                            (child.props.className ? child.props.className + ' ' : '') + 'mt-2',
                        });
                      }
                      return child;
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex h-full flex-shrink-0 items-center pr-2">
            <div className="flex flex-shrink-0 items-center gap-2">
              {/*Espaço do botão Electron */}
              <div
                style={{
                  width: 32,
                  height: 32,
                  minWidth: 32,
                  minHeight: 32,
                  display: 'inline-block',
                }}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-primary-dark mt-2 mb-2 hidden h-full w-full md:inline-flex"
                  >
                    <Icons.GearSettings />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuOptions.map((option, index) => {
                    const IconComponent = option.icon
                      ? Icons[option.icon as keyof typeof Icons]
                      : null;
                    return (
                      <DropdownMenuItem
                        key={index}
                        onSelect={option.onClick}
                        className="flex items-center gap-2 py-2"
                      >
                        {IconComponent && (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <Icons.ByName name={option.icon} />
                            <Icons.ByName
                              name={
                                typeof IconComponent === 'string'
                                  ? IconComponent
                                  : typeof IconComponent === 'function' && IconComponent.name
                                    ? IconComponent.name
                                    : ''
                              }
                            />
                          </span>
                        )}
                        <span className="flex-1">{option.title}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
